// src/pages/HR/reports/PerformanceReviews.js
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, addDoc, updateDoc, doc } from "firebase/firestore";
import { db } from "../../../services/firebase";
import HRCampusSelector from "../../../components/HRCampusSelector";

function exportCSV(filename, rows) {
  const header = ["Employee", "Period", "Score", "Rating", "Reviewer", "Notes", "Campus"];
  const csv = [header, ...rows.map(r => [r.employeeName || r.employee, r.period, r.score, r.rating, r.reviewer, r.notes || "", r.campus])]
    .map(r => r.map(x => `"${String(x || "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length);
  const rows = [];
  for (const line of lines) {
    const cells = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"' ) {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
        else { inQ = !inQ; }
      } else if (ch === "," && !inQ) {
        cells.push(cur); cur = "";
      } else {
        cur += ch;
      }
    }
    cells.push(cur);
    rows.push(cells);
  }
  return rows;
}

function getField(obj, aliases) {
  for (const n of aliases) if (Object.prototype.hasOwnProperty.call(obj, n)) return obj[n];
  return undefined;
}

export default function PerformanceReviews() {
  const [campus, setCampus] = useState(() => localStorage.getItem("hr_reports_campus") || "bulacan");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ employeeName: "", period: "", score: "", rating: "", reviewer: "", notes: "" });

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "employees_reviews"));
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setRows(list);
      } catch {
        setRows([]);
      }
      try {
        const es = await getDocs(collection(db, "employees"));
        setEmployees(es.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch { setEmployees([]); }
    })();
  }, []);

  const scoped = useMemo(() => rows.filter(r => String(r.campus || "") === campus), [rows, campus]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return scoped;
    return scoped.filter(r =>
      String(r.employeeName || r.employee || "").toLowerCase().includes(s) ||
      String(r.rating || "").toLowerCase().includes(s) ||
      String(r.reviewer || "").toLowerCase().includes(s)
    );
  }, [scoped, q]);

  async function reload() {
    const snap = await getDocs(collection(db, "employees_reviews"));
    setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  async function importCSV(file) {
    const text = await file.text();
    const rows = parseCSV(text);
    if (rows.length < 2) { alert("CSV has no data."); return; }
    const headers = rows[0].map(h => String(h).trim().toLowerCase());
    let updated = 0, created = 0;
    const list = [];
    for (let i = 1; i < rows.length; i++) {
      const obj = {};
      rows[i].forEach((v, idx) => obj[headers[idx]] = v);
      const employeeName = getField(obj, ["employee","employee name","employee_name"]) || "";
      const period = getField(obj, ["period","date"]) || "";
      const score = Number(getField(obj, ["score","rating score"]) || 0);
      const rating = getField(obj, ["rating","grade"]) || "";
      const reviewer = getField(obj, ["reviewer","evaluated by","evaluator"]) || "";
      const notes = getField(obj, ["notes","remarks"]) || "";
      const rec = { employeeName, period, score, rating, reviewer, notes, campus };
      list.push(rec);
    }
    // Naive upsert: load all then match by employeeName+period+campus
    const snap = await getDocs(collection(db, "employees_reviews"));
    const existing = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const idx = new Map(existing.map(r => [`${String(r.employeeName||"").toLowerCase()}|${r.period}|${r.campus}`, r.id]));
    for (const r of list) {
      const key = `${String(r.employeeName||"").toLowerCase()}|${r.period}|${r.campus}`;
      const found = idx.get(key);
      if (found) {
        await updateDoc(doc(db, "employees_reviews", found), r);
        updated++;
      } else {
        await addDoc(collection(db, "employees_reviews"), r);
        created++;
      }
    }
    await reload();
    alert(`Import complete — Updated: ${updated}, Created: ${created}.`);
  }

  async function addReview(e) {
    e.preventDefault();
    const rec = { ...form, score: Number(form.score || 0), campus };
    await addDoc(collection(db, "employees_reviews"), rec);
    setShowAdd(false);
    await reload();
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Performance Reviews</h3>
        <div className="d-flex align-items-center gap-2 print-hide">
          <HRCampusSelector selectedCampus={campus} onChange={setCampus} />
          <input className="form-control" style={{ width: 240 }} placeholder="Search employee/rating" value={q} onChange={e => setQ(e.target.value)} />
          <input type="file" accept=".csv" className="form-control" onChange={e => e.target.files?.[0] && importCSV(e.target.files[0])} />
          <button className="btn btn-outline-secondary" onClick={() => window.print()}>Print</button>
          <button className="btn btn-primary" onClick={() => exportCSV(`performance-reviews-${campus}.csv`, filtered)}>Export CSV</button>
          <button className="btn btn-success" onClick={() => setShowAdd(true)}>Add Review</button>
        </div>
      </div>
      <p className="text-muted">Appraisal history with scores and ratings.</p>
      <div className="table-responsive">
        <table className="table table-striped table-bordered">
          <thead className="table-light">
            <tr>
              <th>Employee</th>
              <th>Period</th>
              <th>Score</th>
              <th>Rating</th>
              <th>Reviewer</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="6" className="text-center text-muted">No records</td></tr>
            ) : filtered.map((r, i) => (
              <tr key={i}>
                <td>{r.employeeName || r.employee}</td>
                <td>{r.period}</td>
                <td>{r.score}</td>
                <td>{r.rating}</td>
                <td>{r.reviewer}</td>
                <td>{r.notes || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: "rgba(0,0,0,0.35)", zIndex: 1050 }}>
          <div className="position-absolute top-50 start-50 translate-middle" style={{ width: "min(720px, 95vw)" }}>
            <div className="card shadow-lg">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Add Performance Review</h5>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowAdd(false)}>Close</button>
              </div>
              <div className="card-body">
                <form onSubmit={addReview} className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Employee</label>
                    <select className="form-select" value={form.employeeName} onChange={e => setForm(f => ({ ...f, employeeName: e.target.value }))} required>
                      <option value="">Select employee</option>
                      {employees.filter(e => String(e.campus || "").toLowerCase() === campus).sort((a,b)=>String(a.name||"").localeCompare(String(b.name||""))).map(e => (
                        <option key={e.id} value={e.name || ""}>{e.name || e.email}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Period</label>
                    <input type="date" className="form-control" value={form.period} onChange={e => setForm(f => ({ ...f, period: e.target.value }))} required />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Score</label>
                    <input type="number" step="0.01" className="form-control" value={form.score} onChange={e => setForm(f => ({ ...f, score: e.target.value }))} required />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Rating</label>
                    <select className="form-select" value={form.rating} onChange={e => setForm(f => ({ ...f, rating: e.target.value }))} required>
                      <option value="">Select</option>
                      <option>Exceeds</option>
                      <option>Meets</option>
                      <option>Below</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Reviewer</label>
                    <input className="form-control" value={form.reviewer} onChange={e => setForm(f => ({ ...f, reviewer: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Notes</label>
                    <textarea rows="3" className="form-control" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                  </div>
                  <div className="col-12 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
                    <button className="btn btn-primary" type="submit">Save Review</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
